import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { mesAnnonces, supprimerAnnonce, mettreEnAvant, changerStatut, renouvelerAnnonce } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Car, Plus, Trash2, Eye, Pencil, Star } from 'lucide-react';

const MesAnnonces = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limiteInfo, setLimiteInfo] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    mesAnnonces()
      .then(res => {
        setAnnonces(res.data.annonces);
        const actives = res.data.annonces.filter(a => a.statut === 'active').length;
        const limites = {
          acheteur: 0,
          vendeur_particulier: 10,
          vendeur_pro: null,
          admin: null
        };
        const limite = limites[user.role] !== undefined ? limites[user.role] : null;
        setLimiteInfo({ actives, limite });
      })
      .finally(() => setLoading(false));
  }, [user]);

  const handleSupprimer = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await supprimerAnnonce(id);
      setAnnonces(annonces.filter(a => a.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleMiseEnAvant = async (annonce) => {
    try {
      const res = await mettreEnAvant(annonce.id);
      setAnnonces(annonces.map(a =>
        a.id === annonce.id ? { ...a, est_mise_en_avant: !a.est_mise_en_avant } : a
      ));
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur');
    }
  };

  const handleChangerStatut = async (annonce) => {
    const nouveauStatut = annonce.statut === 'active' ? 'vendue' : 'active';
    try {
      await changerStatut(annonce.id, nouveauStatut);
      setAnnonces(annonces.map(a =>
        a.id === annonce.id ? { ...a, statut: nouveauStatut } : a
      ));
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur');
    }
  };

  const handleRenouveler = async (annonce) => {
    try {
      const res = await renouvelerAnnonce(annonce.id);
      alert(res.data.message);
      setAnnonces(annonces.map(a =>
        a.id === annonce.id ? { ...a, statut: 'active', date_expiration: res.data.annonce.date_expiration } : a
      ));
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-white text-2xl font-black">Mes Annonces</h1>
            <p style={{ color: '#F5A623' }} className="text-sm mt-1">{annonces.length} annonce(s)</p>
          </div>
          <Link
            to="/creer-annonce"
            style={{ background: '#F5A623' }}
            className="text-black px-4 py-2 rounded-xl flex items-center gap-2 font-bold hover:opacity-90 transition"
          >
            <Plus size={16} /> Nouvelle annonce
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Limite annonces */}
        {limiteInfo && limiteInfo.limite !== null && (
          <div style={{ background: '#fffbeb', border: '1px solid #F5A623' }} className="rounded-2xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-gray-700">
                📊 Annonces actives : {limiteInfo.actives} / {limiteInfo.limite || 10}
              </p>
              <span style={{ color: '#F5A623' }} className="text-xs font-bold">
                Plan gratuit
              </span>
            </div>

            {/* Barre de progression */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div
                style={{
                  width: `${Math.min((limiteInfo.actives / (limiteInfo.limite || 10)) * 100, 100)}%`,
                  background: limiteInfo.actives >= (limiteInfo.limite || 10) ? '#ef4444' : '#F5A623'
                }}
                className="h-2 rounded-full transition-all"
              />
            </div>

            {limiteInfo.actives >= (limiteInfo.limite || 10) ? (
              <div className="flex items-center justify-between">
                <p className="text-xs text-red-500 font-semibold">
                  ⚠️ Limite atteinte — Passez à un plan pro pour publier plus
                </p>
                <Link
                  to="/abonnements"
                  style={{ background: '#F5A623' }}
                  className="text-black text-xs font-black px-3 py-1 rounded-full hover:opacity-90 transition"
                >
                  Voir les plans
                </Link>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Il vous reste <strong>{(limiteInfo.limite || 10) - limiteInfo.actives}</strong> annonce(s) disponible(s)
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : annonces.length === 0 ? (
          <div className="text-center py-12">
            <Car size={60} className="mx-auto mb-4 text-gray-200" />
            <p className="font-semibold text-gray-500 mb-4">Vous n'avez pas encore d'annonces</p>
            <Link
              to="/creer-annonce"
              style={{ background: '#F5A623' }}
              className="text-black px-6 py-3 rounded-xl font-bold hover:opacity-90 transition"
            >
              Publier une annonce
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {annonces.map(annonce => (
              <div key={annonce.id} className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">

                  <div className="flex items-start gap-3 sm:flex-1 min-w-0">
                    {/* Photo */}
                    <div className="w-20 h-20 sm:w-24 sm:h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {annonce.photo_principale ? (
                        <img
                          src={getImageUrl(annonce.photo_principale)}
                          alt={annonce.titre}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <Car size={32} className="text-gray-300" />
                      )}
                    </div>

                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 line-clamp-2 leading-snug">{annonce.titre}</h3>
                      <p className="font-semibold text-gray-900 text-sm mt-0.5">
                        {parseInt(annonce.prix).toLocaleString('fr-FR')} FCFA
                      </p>

                      <div className="flex gap-2 mt-1.5 flex-wrap items-center">
                        {/* Badge statut cliquable */}
                        <button
                          onClick={() => handleChangerStatut(annonce)}
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold cursor-pointer ${
                            annonce.statut === 'active' ? 'bg-green-100 text-green-600' :
                            annonce.statut === 'vendue' ? 'bg-gray-100 text-gray-600' :
                            'bg-red-100 text-red-600'
                          }`}
                          title="Cliquer pour changer le statut"
                        >
                          {annonce.statut}
                        </button>

                        {/* Bouton renouveler si expirée */}
                        {(annonce.statut === 'expiree' ||
                          new Date(annonce.date_expiration) < new Date()) && (
                          <button
                            onClick={() => handleRenouveler(annonce)}
                            className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-600 hover:bg-blue-200"
                          >
                            🔄 Renouveler
                          </button>
                        )}

                        {/* Date expiration */}
                        <span className="text-xs text-gray-400">
                          Expire: {new Date(annonce.date_expiration).toLocaleDateString('fr-FR')}
                        </span>

                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Eye size={12} /> {annonce.vues} vues
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 justify-end sm:flex-shrink-0">
                    <Link
                      to={`/annonces/${annonce.id}`}
                      className="p-2 text-blue-400 hover:bg-blue-50 rounded-xl transition"
                      title="Voir"
                    >
                      <Eye size={18} />
                    </Link>
                    <Link
                      to={`/annonces/${annonce.id}/modifier`}
                      className="p-2 text-green-500 hover:bg-green-50 rounded-xl transition"
                      title="Modifier"
                    >
                      <Pencil size={18} />
                    </Link>
                    <button
                      onClick={() => handleMiseEnAvant(annonce)}
                      className={`p-2 rounded-xl transition ${
                        annonce.est_mise_en_avant
                          ? 'text-yellow-500 hover:bg-yellow-50'
                          : 'text-gray-300 hover:bg-gray-50'
                      }`}
                      title="Mise en avant"
                    >
                      <Star size={18} fill={annonce.est_mise_en_avant ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      onClick={() => handleSupprimer(annonce.id)}
                      className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MesAnnonces;